import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createAnuncio, getComunidades, crearVotacion } from '../services/api';
import { useToast } from '../components/Toast';

function NuevoAnuncio() {
  const navigate = useNavigate();
  const toast = useToast();
  const [formData, setFormData] = useState({
    tipo: 'nota',
    titulo: '',
    contenido: '',
    archivo: null,
    comunidad_id: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [comunidades, setComunidades] = useState([]);
  const [user, setUser] = useState(null);

  const [votacionData, setVotacionData] = useState({
    pregunta: '',
    tipo_votacion: 'simple',
    max_opciones: 1,
    opciones: ['', ''],
    fecha_cierre: ''
  });

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const userData = JSON.parse(userStr);
      setUser(userData);
      if (userData.rol === 'administrador' || userData.rol === 'empleado') {
        getComunidades().then(res => setComunidades(res.data)).catch(console.error);
      }
    }
  }, []);

  const esVotacion = formData.tipo === 'votacion';

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setFormData({ ...formData, [name]: files ? files[0] : value });
  };

  const handleVotacionChange = (e) => {
    const { name, value } = e.target;
    if (name === 'tipo_votacion') {
      setVotacionData({
        ...votacionData,
        [name]: value,
        max_opciones: value === 'simple' ? 1 : 2
      });
    } else {
      setVotacionData({ ...votacionData, [name]: value });
    }
  };

  const actualizarOpcion = (index, valor) => {
    const nuevasOpciones = [...votacionData.opciones];
    nuevasOpciones[index] = valor;
    setVotacionData({ ...votacionData, opciones: nuevasOpciones });
  };

  const agregarOpcion = () => {
    setVotacionData({
      ...votacionData,
      opciones: [...votacionData.opciones, '']
    });
  };

  const eliminarOpcion = (index) => {
    if (votacionData.opciones.length <= 2) return;
    const nuevasOpciones = votacionData.opciones.filter((_, i) => i !== index);
    setVotacionData({ ...votacionData, opciones: nuevasOpciones });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (formData.tipo === 'votacion') {
      const opcionesLimpias = votacionData.opciones.filter(o => o.trim());
      if (!votacionData.pregunta || opcionesLimpias.length < 2) {
        const msg = 'Para anuncios de tipo votación necesitas una pregunta y al menos 2 opciones.';
        toast.error(msg);
        setError(msg);
        setLoading(false);
        return;
      }
    }

    try {
      const data = new FormData();
      const tituloFinal = esVotacion ? votacionData.pregunta.trim() : formData.titulo;
      const contenidoFinal = esVotacion ? votacionData.pregunta.trim() : formData.contenido;
      data.append('tipo', formData.tipo);
      data.append('titulo', tituloFinal);
      data.append('contenido', contenidoFinal);
      if (formData.comunidad_id) data.append('comunidad_id', formData.comunidad_id);
      if (formData.archivo && !esVotacion) data.append('archivo', formData.archivo);

      const respuestaAnuncio = await createAnuncio(data);
      const anuncioId = respuestaAnuncio.data.id;

      if (esVotacion && votacionData.pregunta) {
        const opcionesLimpias = votacionData.opciones.filter(o => o.trim());
        if (opcionesLimpias.length >= 2) {
          await crearVotacion({
            anuncio_id: anuncioId,
            pregunta: votacionData.pregunta,
            tipo_votacion: votacionData.tipo_votacion,
            max_opciones: votacionData.max_opciones,
            opciones: opcionesLimpias,
            fecha_cierre: votacionData.fecha_cierre || null
          });
          toast.success('¡Anuncio y votación creados con éxito!');
        }
      } else {
        toast.success('¡Anuncio creado con éxito!');
      }

      navigate('/tablon');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al crear anuncio. Por favor, verifica los datos.');
      setError(err.response?.data?.error || 'Error al crear anuncio. Por favor, verifica los datos.');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getTipoDescripcion = (tipo) => {
    const descripciones = {
      nota: '📌 Avisos y recordatorios breves',
      comunicado: '📢 Información oficial importante',
      convocatoria: '📅 Convocatorias a eventos',
      votacion: '📊 Votaciones y encuestas de la comunidad'
    };
    return descripciones[tipo];
  };

  return (
    <div className="container" style={{ paddingTop: '40px', maxWidth: '800px' }}>
      <h1 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '32px' }}>
        📢 Nuevo Anuncio para el Tablón
      </h1>

      {error && <div className="alert alert-error">{error}</div>}

      <form onSubmit={handleSubmit} className="card">
        <div className="form-group">
          <label className="form-label">Tipo de Anuncio *</label>
          <select
            name="tipo"
            className="form-control"
            value={formData.tipo}
            onChange={handleChange}
            required
          >
            <option value="nota">📌 Nota</option>
            <option value="comunicado">📢 Comunicado</option>
            <option value="convocatoria">📅 Convocatoria</option>
            <option value="votacion">📊 Votación</option>
          </select>
          <small style={{ color: 'var(--text-secondary)', fontSize: '12px', marginTop: '4px', display: 'block' }}>
            {getTipoDescripcion(formData.tipo)}
          </small>
        </div>

        {(user?.rol === 'administrador' || user?.rol === 'empleado') && (
          <div className="form-group">
            <label className="form-label">Comunidad *</label>
            <select name="comunidad_id" className="form-control" value={formData.comunidad_id} onChange={handleChange} required>
              <option value="">Selecciona una comunidad</option>
              {comunidades.map(com => (
                <option key={com.id} value={com.id}>{com.nombre}</option>
              ))}
            </select>
          </div>
        )}

        {!esVotacion && (
          <>
            <div className="form-group">
              <label className="form-label">Título *</label>
              <input
                type="text"
                name="titulo"
                className="form-control"
                value={formData.titulo}
                onChange={handleChange}
                required
                placeholder="Ej: Reunión ordinaria de vecinos"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Contenido *</label>
              <textarea
                name="contenido"
                className="form-control"
                value={formData.contenido}
                onChange={handleChange}
                required
                rows="8"
                placeholder="Escribe aquí el contenido del anuncio..."
              />
            </div>

            <div className="form-group">
              <label className="form-label">Archivo Adjunto (opcional)</label>
              <input
                type="file"
                name="archivo"
                className="form-control"
                onChange={handleChange}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              />
              <small style={{ color: 'var(--text-secondary)', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                Formatos permitidos: PDF, DOC, DOCX, JPG, PNG (máx. 5MB)
              </small>
            </div>
          </>
        )}

        {esVotacion && (
          <div style={{
            padding: '16px',
            background: 'var(--light-color)',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            <div className="form-group">
              <label className="form-label">Pregunta *</label>
              <input
                type="text"
                name="pregunta"
                className="form-control"
                value={votacionData.pregunta}
                onChange={handleVotacionChange}
                placeholder="Ej: ¿Aprobamos el presupuesto de 2024?"
                required
              />
            </div>

            <div className="grid grid-2">
              <div className="form-group">
                <label className="form-label">Tipo de votación</label>
                <select
                  name="tipo_votacion"
                  className="form-control"
                  value={votacionData.tipo_votacion}
                  onChange={handleVotacionChange}
                >
                  <option value="simple">Simple (1 opcion)</option>
                  <option value="multiple">Multiple (varias opciones)</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Fecha de cierre</label>
                <input
                  type="date"
                  name="fecha_cierre"
                  className="form-control"
                  value={votacionData.fecha_cierre}
                  onChange={handleVotacionChange}
                />
              </div>
            </div>

            {votacionData.tipo_votacion === 'multiple' && (
              <div className="form-group">
                <label className="form-label">Maximo de opciones seleccionables</label>
                <select
                  name="max_opciones"
                  className="form-control"
                  value={votacionData.max_opciones}
                  onChange={handleVotacionChange}
                >
                  <option value="2">2 opciones</option>
                  <option value="3">3 opciones</option>
                  <option value="4">4 opciones</option>
                </select>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Opciones de votacion *</label>
              {votacionData.opciones.map((opcion, index) => (
                <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                  <input
                    type="text"
                    className="form-control"
                    value={opcion}
                    onChange={(e) => actualizarOpcion(index, e.target.value)}
                    placeholder={`Opcion ${index + 1}`}
                    required={index < 2}
                  />
                  {votacionData.opciones.length > 2 && (
                    <button
                      type="button"
                      className="btn btn-outline"
                      onClick={() => eliminarOpcion(index)}
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
              <button type="button" className="btn btn-outline" onClick={agregarOpcion}>+ Añadir opción</button>
            </div>
          </div>
        )}

        <div className="alert alert-info">
          <strong>ℹ️ Nota:</strong> Al publicar este anuncio, todos los vecinos de la comunidad recibirán una notificación.
        </div>

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={() => navigate('/tablon')}
            className="btn btn-outline"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={loading}
            className="btn btn-primary"
          >
            {loading ? 'Publicando...' : '📢 Publicar Anuncio'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default NuevoAnuncio;
